/* ============================================================
   FEHU ᚠ — the wealth rune · Lund Studio conversion engine
   v1.0 · © 2026 Lund Studio LLC · lundstudio.co
   ------------------------------------------------------------
   One grammar for every purchase control on a site.
   Zero dependencies · no build step · no network · no cookies ·
   no storage. State lives in memory; inventory truth lives in
   the platform. This engine renders numbers — it never invents
   them, and it ships no countdown API on purpose.
   ------------------------------------------------------------
   FEHU.attach(cfg)          dress existing controls in the grammar
   FEHU.busy(el)             click registered, working
   FEHU.aboard(el[, opts])   confirmed in the cart (auto-restores)
   FEHU.gone(el[, label])    sold out — the honest headstone
   FEHU.restore(el)          back to rest
   FEHU.stock(el, o)         render a live-count meter {left,total}
   FEHU.bar(opts)            the launch bar (post-add, one job)
   FEHU.setCount(n)          sync bar with the real cart count
   FEHU.audit()              warn when two primaries share a screen
   Events: 'fehu:aboard' · 'fehu:launch' (bubbling CustomEvents)
   ============================================================ */
(function (root, factory) {
  if (typeof module === "object" && module.exports) module.exports = factory();
  else root.FEHU = factory();
})(typeof self !== "undefined" ? self : this, function () {
  "use strict";

  var DOC = typeof document !== "undefined" ? document : null;
  var count = 0;                       // aboard events this session (bar display)
  var barEl = null, barCfg = null, mo = null;

  var DEFAULTS = {
    primary: "[data-fehu]",            // selectors are per-site; see attach()
    second: "[data-fehu-second]",
    quiet: "[data-fehu-quiet]",
    hue: "#00CE7E",                    // grove green — the studio's action ink
    hueMap: null,                      // e.g. { green:"#00CE7E", pink:"#EA41AA" }
    hueKey: "data-fehu-vessel",        // attribute looked up in hueMap
    busyText: "\u00B7\u00B7\u00B7",
    aboardText: "ABOARD \u2713",
    goneText: "GONE FOR GOOD",
    subline: "",                       // the truth line, e.g. "$33 + SHIPPING"
    auto: false,                       // true: click runs busy→aboard (demo mode)
    autoDelay: 260,                    // fake latency for auto mode only
    restoreAfter: 1400,
    observe: true                      // re-dress controls built later by JS
  };
  var cfg = null;

  function merge(a, b) { var o = {}, k; for (k in a) o[k] = a[k]; for (k in b || {}) o[k] = b[k]; return o; }
  function all(sel, scope) { if (!DOC || !sel) return []; try { return Array.prototype.slice.call((scope || DOC).querySelectorAll(sel)); } catch (e) { return []; } }

  /* hue resolution: own attr → hueMap via hueKey → ancestor attr → default */
  function hueFor(el) {
    if (!el) return cfg.hue;
    var h = el.getAttribute("data-fehu-hue");
    if (h) return h;
    if (cfg.hueMap) {
      var key = el.getAttribute(cfg.hueKey) || el.getAttribute("data-color");
      if (key && cfg.hueMap[key]) return cfg.hueMap[key];
    }
    var up = el.closest ? el.closest("[data-fehu-hue]") : null;
    if (up) return up.getAttribute("data-fehu-hue");
    return cfg.hue;
  }

  function keepLabel(el) { if (el.getAttribute("data-fehu-label") == null) el.setAttribute("data-fehu-label", el.textContent); }
  function label(el, t) { keepLabel(el); el.textContent = t; }
  function unlabel(el) { var t = el.getAttribute("data-fehu-label"); if (t != null) el.textContent = t; }

  function dress(el, role) {
    if (!el || el.getAttribute("data-fehu-dressed")) return;
    el.setAttribute("data-fehu-dressed", role);
    el.classList.add(role === "second" ? "fehu--second" : role === "quiet" ? "fehu--quiet" : "fehu");
    el.style.setProperty("--fehu-hue", hueFor(el));
    if (role === "primary") {
      var sub = el.getAttribute("data-fehu-sub");
      if (sub != null) {
        var s = DOC.createElement("span");
        s.className = "fehu-sub";
        s.textContent = sub || cfg.subline || "";
        if (s.textContent) el.insertAdjacentElement("afterend", s);
      }
    }
  }

  function dressAll(scope) {
    all(cfg.primary, scope).forEach(function (el) { dress(el, "primary"); });
    all(cfg.second, scope).forEach(function (el) { dress(el, "second"); });
    all(cfg.quiet, scope).forEach(function (el) { dress(el, "quiet"); });
    all("[data-fehu-stock]", scope).forEach(function (el) {
      if (el.getAttribute("data-fehu-stock-done")) return;
      var m = /^(\d+)\s*\/\s*(\d+)$/.exec(el.getAttribute("data-fehu-stock") || "");
      if (m) stock(el, { left: +m[1], total: +m[2], label: el.getAttribute("data-fehu-stock-label") || undefined });
    });
  }

  /* ---- states -------------------------------------------------- */
  function busy(el) { if (!el) return; el.setAttribute("data-fehu-state", "busy"); label(el, cfg.busyText); }
  function restore(el) { if (!el) return; el.removeAttribute("data-fehu-state"); unlabel(el); }
  function aboard(el, opts) {
    opts = opts || {};
    count = typeof opts.count === "number" ? opts.count : count + 1;
    if (el) {
      el.setAttribute("data-fehu-state", "aboard");
      label(el, opts.text || cfg.aboardText);
      setTimeout(function () { restore(el); }, opts.restoreAfter || cfg.restoreAfter);
    }
    barShow();
    if (DOC && el && el.dispatchEvent) {
      el.dispatchEvent(new CustomEvent("fehu:aboard", { bubbles: true, detail: { el: el, count: count } }));
    }
    return count;
  }
  function gone(el, text) {
    if (!el) return;
    el.setAttribute("data-fehu-state", "gone");
    label(el, text || cfg.goneText);
    if (el.tagName === "BUTTON") el.disabled = true;
    el.setAttribute("aria-disabled", "true");
  }

  /* ---- the count: honest scarcity only ------------------------- */
  function stock(el, o) {
    if (!el || !o || !DOC) return el;
    var left = Math.max(0, o.left | 0), total = Math.max(1, o.total | 0);
    if (left > total) left = total;                 // never overstate
    var lab = o.label || (left === 0
      ? (o.goneLabel || "GONE FOR GOOD")
      : left + " OF " + total + (o.tail ? " \u00B7 " + o.tail : ""));
    el.classList.add("fehu-stock");
    el.setAttribute("data-fehu-stock-done", "1");
    el.innerHTML =
      '<span class="fehu-stock-label"><span></span><span></span></span>' +
      '<span class="fehu-stock-track"><span class="fehu-stock-fill"></span></span>';
    el.querySelector(".fehu-stock-label span").textContent = lab;
    el.querySelectorAll(".fehu-stock-label span")[1].textContent =
      o.right != null ? o.right : Math.round((left / total) * 100) + "%";
    var fill = el.querySelector(".fehu-stock-fill");
    if (o.hue) fill.style.setProperty("--fehu-hue", o.hue);
    requestAnimationFrame(function () { fill.style.width = (left / total) * 100 + "%"; });
    return el;
  }

  /* ---- the launch bar: appears after the first aboard ----------- */
  function bar(opts) {
    if (!DOC) return null;
    barCfg = merge(barCfg || {
      line: function (n) { return n + " ABOARD"; },
      label: "LAUNCH \u2192",
      href: null, onLaunch: null, hue: null, dismiss: true
    }, opts);
    if (!barEl) {
      barEl = DOC.createElement("div");
      barEl.className = "fehu-bar";
      barEl.setAttribute("role", "region");
      barEl.setAttribute("aria-label", "Cart");
      barEl.innerHTML =
        '<span class="fehu-bar-line"></span>' +
        '<a class="fehu" href="#"></a>' +
        '<button class="fehu-bar-x" type="button" aria-label="Dismiss">\u00D7</button>';
      DOC.body.appendChild(barEl);
      barEl.querySelector(".fehu").addEventListener("click", function (ev) {
        if (barCfg.onLaunch) { ev.preventDefault(); barCfg.onLaunch(count); }
        barEl.dispatchEvent(new CustomEvent("fehu:launch", { bubbles: true, detail: { count: count } }));
      });
      barEl.querySelector(".fehu-bar-x").addEventListener("click", function () {
        barEl.removeAttribute("data-fehu-open");
      });
    }
    var go = barEl.querySelector(".fehu");
    go.textContent = barCfg.label;
    go.setAttribute("href", barCfg.href || "#");
    if (barCfg.hue) go.style.setProperty("--fehu-hue", barCfg.hue);
    barEl.querySelector(".fehu-bar-x").style.display = barCfg.dismiss ? "" : "none";
    barPaint();
    return barEl;
  }
  function barPaint() {
    if (!barEl || !barCfg) return;
    var line = typeof barCfg.line === "function" ? barCfg.line(count) : barCfg.line;
    barEl.querySelector(".fehu-bar-line").textContent = line;
  }
  function barShow() { if (!barEl) return; barPaint(); if (count > 0) barEl.setAttribute("data-fehu-open", "1"); }
  function setCount(n) { count = Math.max(0, n | 0); barShow(); if (count === 0 && barEl) barEl.removeAttribute("data-fehu-open"); return count; }

  /* ---- audit: one primary per screen is a rule, not a vibe ------ */
  function audit() {
    if (!DOC) return [];
    var seen = {}, flagged = [];
    all(cfg.primary).forEach(function (el) {
      if (!(el.offsetWidth || el.offsetHeight)) return;   // hidden doesn't count
      if (el.closest && el.closest("[data-fehu-bay]")) return; // a bay: one primary per product card, by design
      var sec = el.closest("section,[data-deck],main,body") || DOC.body;
      var key = sec.id || sec.getAttribute("data-deck") || sec.tagName + "#" + Array.prototype.indexOf.call(DOC.querySelectorAll(sec.tagName), sec);
      (seen[key] = seen[key] || []).push(el);
    });
    Object.keys(seen).forEach(function (k) {
      if (seen[k].length > 1) {
        flagged.push({ section: k, primaries: seen[k].length });
        if (typeof console !== "undefined") console.warn("[FEHU] " + seen[k].length + " primaries share \u201C" + k + "\u201D \u2014 demote all but one to .fehu--second");
      }
    });
    return flagged;
  }

  /* ---- attach --------------------------------------------------- */
  function attach(userCfg) {
    cfg = merge(DEFAULTS, userCfg);
    if (!DOC) return api;
    dressAll(DOC);
    if (cfg.auto) {
      DOC.addEventListener("click", function (ev) {
        var el = ev.target && ev.target.closest ? ev.target.closest('[data-fehu-dressed="primary"]') : null;
        if (!el || el.getAttribute("data-fehu-state")) return;
        busy(el);
        setTimeout(function () { aboard(el); }, cfg.autoDelay);
      });
    }
    if (cfg.observe && typeof MutationObserver !== "undefined" && !mo) {
      mo = new MutationObserver(function () { dressAll(DOC); });
      mo.observe(DOC.documentElement, { childList: true, subtree: true });
    }
    return api;
  }

  var api = {
    version: "1.0",
    attach: attach, dress: dress, busy: busy, aboard: aboard, gone: gone,
    restore: restore, stock: stock, bar: bar, setCount: setCount,
    count: function () { return count; }, audit: audit, hueFor: hueFor
  };
  return api;
});
